import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import { useSeo } from '../hooks/useSeo';
import { PublicLayout } from '../components/public/PublicLayout';
import { LEGAL_PATHS, type LegalDocumentKind } from '../legal/paths';
import deUrl from '../legal/de.json?url';
import enUrl from '../legal/en.json?url';
import frUrl from '../legal/fr.json?url';
import itUrl from '../legal/it.json?url';
import nlUrl from '../legal/nl.json?url';

/** Kontaktadresse fuer Datenschutz- und Vertragsanliegen; ersetzt {{email}} in den Texten. */
const LEGAL_CONTACT_EMAIL = 'info@canebase.ch';
/** Stand beider Rechtstexte - bei jeder inhaltlichen Aenderung anpassen. */
const LEGAL_UPDATED = '2026-10-19';

/** Ein Absatz (string) oder eine Aufzaehlung (string[]). "**Text**" wird fett dargestellt. */
type LegalBlock = string | string[];

interface LegalDocumentContent {
  title: string;
  intro: string;
  sections: { title: string; body: LegalBlock[] }[];
}

interface LegalContent {
  updated: string;
  contents: string;
  /** Hinweis, dass die deutsche Fassung massgebend ist - in der deutschen Fassung leer. */
  bindingNote: string;
  terms: LegalDocumentContent;
  privacy: LegalDocumentContent;
}

// Die Texte sind lang und werden nur hier gebraucht - deshalb pro Sprache nachgeladen statt wie die
// uebrigen Uebersetzungen fest im i18n-Bundle. Als JSON-Asset (?url) statt per import(): als JS-Chunk
// wuerde Rollup sie wegen experimentalMinChunkSize (vite.config.ts) mit fremden kleinen Chunks
// zusammenlegen, z.B. dem pdf.worker-Chunk, der dann bei jeder PDF-Vorschau die AGB mitlaedt.
const CONTENT_URLS: Record<SupportedLanguage, string> = {
  de: deUrl,
  en: enUrl,
  fr: frUrl,
  it: itUrl,
  nl: nlUrl,
};

const loadContent = async (lang: SupportedLanguage): Promise<LegalContent> => {
  const response = await fetch(CONTENT_URLS[lang]);
  if (!response.ok) throw new Error(`Rechtstexte (${lang}) nicht ladbar: ${response.status}`);
  return response.json();
};

const toSupportedLanguage = (lang: string | undefined): SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(lang ?? '') ? (lang as SupportedLanguage) : 'de';

const INTERNAL_LINKS: Record<string, string> = {
  '/agb': LEGAL_PATHS.terms,
  '/datenschutz': LEGAL_PATHS.privacy,
};

const LINK_PATTERN = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+|\/agb\b|\/datenschutz\b|www\.edoeb\.admin\.ch)/g;
const linkClass = 'text-primary-300 underline-offset-2 hover:text-primary-200 hover:underline';

/** Macht E-Mail-Adressen, Verweise auf die Rechtstexte und die EDOeB-Adresse klickbar. */
const renderLinks = (text: string, keyPrefix: string): React.ReactNode[] =>
  text.split(LINK_PATTERN).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (i % 2 === 0) return part;
    if (INTERNAL_LINKS[part]) {
      return (
        <Link key={key} to={INTERNAL_LINKS[part]} className={linkClass}>
          www.canebase.ch{part}
        </Link>
      );
    }
    if (part.startsWith('www.')) {
      return (
        <a key={key} href={`https://${part}`} target="_blank" rel="noopener noreferrer" className={linkClass}>
          {part}
        </a>
      );
    }
    return (
      <a key={key} href={`mailto:${part}`} className={linkClass}>
        {part}
      </a>
    );
  });

const renderInline = (text: string): React.ReactNode[] =>
  text
    .replace(/\{\{email\}\}/g, LEGAL_CONTACT_EMAIL)
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) =>
      i % 2 === 1 ? (
        <strong key={i} className="font-semibold text-dark-100">
          {renderLinks(part, `b${i}`)}
        </strong>
      ) : (
        <React.Fragment key={i}>{renderLinks(part, `t${i}`)}</React.Fragment>
      )
    );

const LegalDocument: React.FC<{ kind: LegalDocumentKind }> = ({ kind }) => {
  const { i18n } = useTranslation();
  const lang = toSupportedLanguage(i18n.resolvedLanguage);
  const [content, setContent] = useState<{ lang: SupportedLanguage; data: LegalContent } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Faellt eine Uebersetzung aus, die massgebende deutsche Fassung zeigen statt einer leeren Seite.
    loadContent(lang)
      .then((data) => ({ lang, data }))
      .catch(() => loadContent('de').then((data) => ({ lang: 'de' as const, data })))
      .then((loaded) => {
        if (!cancelled) setContent(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // "instant": html hat scroll-behavior: smooth, und eine laufende Scroll-Animation bricht ab, wenn
  // gleichzeitig die Texte einer anderen Sprache nachgeladen werden.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [kind]);

  const data = content?.data;
  const doc = data?.[kind];

  useSeo({
    title: doc ? `${doc.title} – canebase` : 'canebase',
    description: doc?.intro,
    path: LEGAL_PATHS[kind],
    lang: content?.lang,
  });

  if (!data || !doc) {
    return <div className="min-h-[60vh]" aria-busy="true" />;
  }

  const updated = data.updated.replace(
    '{{date}}',
    new Intl.DateTimeFormat(content.lang, { dateStyle: 'long' }).format(new Date(`${LEGAL_UPDATED}T00:00:00`))
  );

  return (
    <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:py-14">
      <nav className="mb-8 inline-flex rounded-xl border border-dark-700/70 bg-dark-800/40 p-1 text-sm">
        {(Object.keys(LEGAL_PATHS) as LegalDocumentKind[]).map((k) => (
          <Link
            key={k}
            to={LEGAL_PATHS[k]}
            aria-current={k === kind ? 'page' : undefined}
            className={clsx(
              'rounded-lg px-3 py-1.5 font-medium transition-colors',
              k === kind ? 'bg-dark-700 text-dark-50' : 'text-dark-300 hover:text-dark-50'
            )}
          >
            {data[k].title}
          </Link>
        ))}
      </nav>

      <h1 className="text-balance text-3xl font-extrabold tracking-tight text-dark-50 sm:text-4xl">{doc.title}</h1>
      <p className="mt-3 text-sm text-dark-400">{updated}</p>
      {data.bindingNote && (
        <p className="mt-4 rounded-lg border border-dark-700/70 bg-dark-800/40 px-4 py-3 text-sm text-dark-300">
          {data.bindingNote}
        </p>
      )}
      <p className="mt-6 text-base leading-relaxed text-dark-200">{renderInline(doc.intro)}</p>

      <nav aria-label={data.contents} className="mt-8 rounded-xl border border-dark-700/70 bg-dark-800/30 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-dark-400">{data.contents}</h2>
        {/* Spalten statt Grid, damit die Nummerierung spaltenweise (1-10 | 11-20) statt zeilenweise laeuft */}
        <ol className="mt-3 gap-x-8 text-sm sm:columns-2">
          {doc.sections.map((section, i) => (
            <li key={i} className="mb-1.5 break-inside-avoid">
              <a href={`#${kind}-${i + 1}`} className="text-dark-300 transition-colors hover:text-dark-50">
                {section.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {doc.sections.map((section, i) => (
        <section key={i} id={`${kind}-${i + 1}`} className="mt-10 scroll-mt-24">
          <h2 className="text-lg font-bold text-dark-50 sm:text-xl">{section.title}</h2>
          <div className="mt-3 space-y-3 text-[0.95rem] leading-relaxed text-dark-300">
            {section.body.map((block, j) =>
              Array.isArray(block) ? (
                <ul key={j} className="list-disc space-y-1.5 pl-5 marker:text-dark-500">
                  {block.map((item, k) => (
                    <li key={k}>{renderInline(item)}</li>
                  ))}
                </ul>
              ) : (
                <p key={j}>{renderInline(block)}</p>
              )
            )}
          </div>
        </section>
      ))}
    </article>
  );
};

/** AGB bzw. Datenschutzerklaerung. Abgemeldet im Rahmen der oeffentlichen Seiten, angemeldet
 * innerhalb der App-Shell (ohne Login-/Registrierungs-Kopfzeile). */
const LegalPage: React.FC<{ kind: LegalDocumentKind; public?: boolean }> = ({ kind, public: isPublic = false }) =>
  isPublic ? <PublicLayout>{() => <LegalDocument kind={kind} />}</PublicLayout> : <LegalDocument kind={kind} />;

export default LegalPage;
